import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../config';

const authApiUrl = `${API_URL}/Auth`;

// Helper function to decode JWT
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const getUserRoleFromToken = (decodedToken) =>
  decodedToken
    ? decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || decodedToken['role']
    : null;

const getUsernameFromToken = (decodedToken) =>
  decodedToken
    ? decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || decodedToken['name'] || decodedToken['unique_name']
    : null;

const isTokenExpired = (decodedToken) => {
  if (!decodedToken?.exp) return true;
  return decodedToken.exp * 1000 <= Date.now();
};

// Async thunk for user registration
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${authApiUrl}/register`, userData);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (typeof data === 'string') {
          return rejectWithValue(data);
        }
        if (data.message) {
          return rejectWithValue(data.message);
        }
        if (data.title) {
          return rejectWithValue(data.title);
        }
        return rejectWithValue(data);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for user login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${authApiUrl}/login`, credentials);
      const token = response.data;
      localStorage.setItem('jwtToken', token);
      return token;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

const getTokenDetails = () => {
  const token = localStorage.getItem('jwtToken');
  if (token) {
    const decodedToken = parseJwt(token);
    if (!decodedToken || isTokenExpired(decodedToken)) {
      localStorage.removeItem('jwtToken');
      return { token: null, isLoggedIn: false, userRole: null, username: null };
    }

    return {
      token,
      isLoggedIn: true,
      userRole: getUserRoleFromToken(decodedToken),
      username: getUsernameFromToken(decodedToken),
    };
  }
  return { token: null, isLoggedIn: false, userRole: null, username: null };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    ...getTokenDetails(),
    status: 'idle',
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.token = null;
      state.isLoggedIn = false;
      state.userRole = null;
      state.username = null;
      localStorage.removeItem('jwtToken');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.token = action.payload;
        const decodedToken = parseJwt(action.payload);
        if (!decodedToken || isTokenExpired(decodedToken)) {
          state.token = null;
          state.isLoggedIn = false;
          state.userRole = null;
          state.username = null;
          localStorage.removeItem('jwtToken');
          state.error = 'Login token is invalid. Please try again.';
          return;
        }

        state.isLoggedIn = true;
        state.userRole = getUserRoleFromToken(decodedToken);
        state.username = getUsernameFromToken(decodedToken);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.token = null;
        state.isLoggedIn = false;
        state.userRole = null;
        state.username = null;
      });
  },
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
